import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

function ZoneDInterventionPage() {
    const navigate = useNavigate();
    const [hoveredRegion, setHoveredRegion] = useState(null);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    const handleRegionClick = (path) => {
        navigate(path);
    };

    const regions = [
        {
            code: '75',
            name: 'Paris',
            fullName: 'Paris (75)',
            description: 'Expertise à domicile dans tous les arrondissements parisiens',
            color: '#C0A26B',
            path: '/zone-d-intervention/75',
            gridPosition: { column: 2, row: 1 }
        },
        {
            code: '93',
            name: 'Seine-Saint-Denis',
            fullName: 'Seine-Saint-Denis (93)',
            description: 'Service complet dans le département 93',
            color: '#d4b875',
            path: '/zone-d-intervention/93',
            gridPosition: { column: 3, row: 1 }
        },
        {
            code: '94',
            name: 'Val-de-Marne',
            fullName: 'Val-de-Marne (94)',
            description: 'Déplacement gratuit dans le Val-de-Marne',
            color: '#e6c889',
            path: '/zone-d-intervention/94',
            gridPosition: { column: 3, row: 2 }
        },
        {
            code: '77',
            name: 'Seine-et-Marne',
            fullName: 'Seine-et-Marne (77)',
            description: 'Expertise en Seine-et-Marne',
            color: '#f0d49d',
            path: '/zone-d-intervention/77',
            gridPosition: { column: 2, row: 2 }
        },
        {
            code: '91',
            name: 'Essonne',
            fullName: 'Essonne (91)',
            description: 'Service professionnel en Essonne',
            color: '#a67c52',
            path: '/zone-d-intervention/91',
            gridPosition: { column: 1, row: 2 }
        },
        {
            code: '78',
            name: 'Yvelines',
            fullName: 'Yvelines (78)',
            description: 'Intervention dans les Yvelines',
            color: '#b8956b',
            path: '/zone-d-intervention/78',
            gridPosition: { column: 1, row: 1 }
        }
    ];

    return (
        <div className="zone-page">
            <Helmet>
                <title>Zones d'Intervention | Antiquaire Expert Paris - Île-de-France</title>
                <meta name="description" content="Découvrez nos zones d'intervention en Île-de-France. Service d'expertise et achat d'antiquités à domicile dans Paris et la région parisienne." />
            </Helmet>

            <div className="page-container">
                <header className="page-header">
                    <div className={`title-section ${isVisible ? 'animate-in' : ''}`}>
                        <h1>Zones d'Intervention</h1>
                        <p className="subtitle">
                            Expertise et achat d'antiquités à domicile en Île-de-France
                        </p>
                        <p className="description">
                            Sélectionnez un département pour découvrir nos services dans votre région
                        </p>
                    </div>
                </header>

                <main className="map-section">
                    <div className="map-container">
                        <div className="map-title">
                            <h2>Région Parisienne</h2>
                            <p>Cliquez sur un département</p>
                        </div>
                        
                        <div className="interactive-map">
                            {regions.map((region, index) => (
                                <div
                                    key={region.code}
                                    className={`region-card region-${region.code} ${isVisible ? 'animate-region' : ''}`}
                                    style={{ 
                                        '--delay': `${index * 0.1}s`,
                                        '--accent-color': region.color,
                                        '--grid-column': region.gridPosition.column,
                                        '--grid-row': region.gridPosition.row
                                    }}
                                    onClick={() => handleRegionClick(region.path)}
                                    onMouseEnter={() => setHoveredRegion(region.code)}
                                    onMouseLeave={() => setHoveredRegion(null)}
                                >
                                    <div className="region-content">
                                        <div className="region-number">{region.code}</div>
                                        <h3 className="region-name">{region.name}</h3>
                                        <p className="region-desc">{region.description}</p>
                                    </div>
                                    <div className="region-glow"></div>
                                    <div className="click-indicator">
                                        <span>Cliquer pour en savoir plus</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {hoveredRegion && (
                            <div className="region-info">
                                <h3>
                                    {regions.find(r => r.code === hoveredRegion)?.fullName}
                                </h3>
                                <p>
                                    {regions.find(r => r.code === hoveredRegion)?.description}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="service-highlights">
                        <div className="highlight-card">
                            <h3>Service Gratuit</h3>
                            <p>Déplacement et expertise gratuits dans toute l'Île-de-France</p>
                        </div>
                        <div className="highlight-card">
                            <h3>Réactivité</h3>
                            <p>Intervention rapide sous 48h dans tous les départements</p>
                        </div>
                        <div className="highlight-card">
                            <h3>Expertise</h3>
                            <p>Évaluation professionnelle sur place par un expert qualifié</p>
                        </div>
                    </div>
                </main>
            </div>

            <style jsx>{`
                .zone-page {
                    min-height: 100vh;
                    background: linear-gradient(135deg, #f2f5f7 0%, #e8ecef 100%);
                    padding-top: 80px;
                }

                .page-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 0 24px;
                }

                .page-header {
                    text-align: center;
                    margin-bottom: 80px;
                    padding: 60px 0;
                }

                .title-section {
                    opacity: 0;
                    transform: translateY(30px);
                    transition: all 0.8s ease;
                }

                .animate-in {
                    opacity: 1 !important;
                    transform: translateY(0) !important;
                }

                .page-header h1 {
                    font-size: 3.5rem;
                    font-weight: 700;
                    color: #1f2b2a;
                    margin: 0 0 20px;
                    letter-spacing: -0.02em;
                }

                .subtitle {
                    font-size: 1.4rem;
                    color: #C0A26B;
                    margin: 0 0 16px;
                    font-style: italic;
                    font-weight: 500;
                }

                .description {
                    font-size: 1.2rem;
                    color: #44504f;
                    margin: 0;
                    max-width: 600px;
                    margin-left: auto;
                    margin-right: auto;
                }

                .map-section {
                    margin-bottom: 80px;
                }

                .map-container {
                    background: #fff;
                    border-radius: 24px;
                    padding: 48px;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.08);
                    margin-bottom: 60px;
                    position: relative;
                    overflow: hidden;
                }

                .map-container::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: radial-gradient(circle at 20% 80%, rgba(192, 162, 107, 0.05) 0%, transparent 50%);
                    z-index: 0;
                }

                .map-title {
                    text-align: center;
                    margin-bottom: 48px;
                    position: relative;
                    z-index: 2;
                }

                .map-title h2 {
                    font-size: 2.4rem;
                    color: #1f2b2a;
                    margin: 0 0 8px;
                    font-weight: 600;
                }

                .map-title p {
                    color: #44504f;
                    font-size: 1.1rem;
                    margin: 0;
                }

                .interactive-map {
                    display: grid;
                    grid-template-columns: repeat(3, 1fr);
                    grid-template-rows: repeat(2, 1fr);
                    gap: 20px;
                    max-width: 700px;
                    margin: 0 auto 40px;
                    position: relative;
                    z-index: 2;
                }

                .region-card {
                    position: relative;
                    background: linear-gradient(135deg, #fff 0%, #f8fafb 100%);
                    border-radius: 20px;
                    padding: 32px 24px;
                    cursor: pointer;
                    transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
                    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
                    border: 2px solid transparent;
                    opacity: 0;
                    transform: translateY(30px) scale(0.95);
                    overflow: hidden;
                    grid-column: var(--grid-column);
                    grid-row: var(--grid-row);
                }

                .animate-region {
                    opacity: 1 !important;
                    transform: translateY(0) scale(1) !important;
                    transition-delay: var(--delay);
                }

                .region-card:hover {
                    transform: translateY(-8px) scale(1.02);
                    box-shadow: 0 12px 40px rgba(0,0,0,0.15);
                    border-color: var(--accent-color);
                }

                .region-glow {
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle, var(--accent-color) 0%, transparent 70%);
                    opacity: 0;
                    transition: opacity 0.3s ease;
                    z-index: 0;
                }

                .region-card:hover .region-glow {
                    opacity: 0.1;
                }

                .region-content {
                    position: relative;
                    z-index: 2;
                    text-align: center;
                }

                .region-number {
                    display: inline-block;
                    background: var(--accent-color);
                    color: #20453d;
                    font-size: 1.4rem;
                    font-weight: 700;
                    padding: 8px 16px;
                    border-radius: 12px;
                    margin-bottom: 16px;
                    min-width: 60px;
                }

                .region-name {
                    font-size: 1.3rem;
                    font-weight: 600;
                    color: #1f2b2a;
                    margin: 0 0 12px;
                    line-height: 1.2;
                }

                .region-desc {
                    font-size: 0.95rem;
                    color: #44504f;
                    margin: 0;
                    line-height: 1.5;
                    opacity: 0.8;
                }

                .click-indicator {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    background: linear-gradient(to top, var(--accent-color), transparent);
                    padding: 12px;
                    opacity: 0;
                    transform: translateY(100%);
                    transition: all 0.3s ease;
                }

                .region-card:hover .click-indicator {
                    opacity: 1;
                    transform: translateY(0);
                }

                .click-indicator span {
                    color: #20453d;
                    font-size: 0.85rem;
                    font-weight: 600;
                    text-align: center;
                    display: block;
                }

                .region-info {
                    background: linear-gradient(135deg, #20453d 0%, #1a3a33 100%);
                    border-radius: 16px;
                    padding: 24px;
                    text-align: center;
                    color: #fff;
                    animation: fadeInUp 0.3s ease;
                }

                .region-info h3 {
                    color: #C0A26B;
                    margin: 0 0 8px;
                    font-size: 1.2rem;
                }

                .region-info p {
                    color: rgba(255,255,255,0.9);
                    margin: 0;
                    font-size: 1rem;
                }

                .service-highlights {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 24px;
                    margin-top: 60px;
                }

                .highlight-card {
                    background: #fff;
                    border-radius: 20px;
                    padding: 32px 24px;
                    text-align: center;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
                    transition: all 0.3s ease;
                    border-top: 4px solid #C0A26B;
                }

                .highlight-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 32px rgba(0,0,0,0.12);
                }

                .highlight-card h3 {
                    font-size: 1.4rem;
                    color: #1f2b2a;
                    margin: 0 0 12px;
                    font-weight: 600;
                }

                .highlight-card p {
                    color: #44504f;
                    margin: 0;
                    line-height: 1.6;
                }

                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @media (max-width: 1024px) {
                    .interactive-map {
                        max-width: 600px;
                        gap: 16px;
                    }

                    .region-card {
                        padding: 28px 20px;
                    }
                }

                @media (max-width: 768px) {
                    .zone-page {
                        padding-top: 100px;
                    }

                    .page-container {
                        padding: 0 20px;
                    }

                    .page-header {
                        margin-bottom: 60px;
                        padding: 40px 0;
                    }

                    .page-header h1 {
                        font-size: 2.5rem;
                    }

                    .subtitle {
                        font-size: 1.2rem;
                    }

                    .description {
                        font-size: 1.1rem;
                    }

                    .map-container {
                        padding: 32px 24px;
                    }

                    .interactive-map {
                        grid-template-columns: repeat(2, 1fr);
                        grid-template-rows: repeat(3, 1fr);
                        max-width: 500px;
                        gap: 16px;
                    }

                    .region-card {
                        padding: 24px 16px;
                        grid-column: auto !important;
                        grid-row: auto !important;
                    }

                    .service-highlights {
                        grid-template-columns: 1fr;
                        gap: 16px;
                    }

                    .highlight-card {
                        padding: 24px 20px;
                    }
                }

                @media (max-width: 480px) {
                    .zone-page {
                        padding-top: 120px;
                    }

                    .page-container {
                        padding: 0 16px;
                    }

                    .page-header h1 {
                        font-size: 2rem;
                    }

                    .map-container {
                        padding: 24px 16px;
                    }

                    .interactive-map {
                        grid-template-columns: 1fr;
                        grid-template-rows: auto;
                        max-width: 350px;
                        gap: 12px;
                    }

                    .region-card {
                        padding: 20px 16px;
                    }

                    .region-number {
                        font-size: 1.2rem;
                        padding: 6px 12px;
                    }

                    .region-name {
                        font-size: 1.1rem;
                    }

                    .region-desc {
                        font-size: 0.9rem;
                    }
                }
            `}</style>
        </div>
    );
}

export default ZoneDInterventionPage;
